import { ArrowLeft, ArrowRight, Eye, Route, Search, X } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import {
  Banner,
  Button,
  Card,
  Drawer,
  EmptyState,
  ErrorState,
  SearchField,
  SegmentedControl,
  SelectField,
  Skeleton,
} from '@shared/design-system/ui'
import {
  useCheckoutTraffic,
  type TrafficFilters,
  type TrafficItem,
  type TrafficView,
} from '../api/checkout-traffic.api'
import { declaredPageComparison, redirectLabel } from '../lib/checkout-traffic'
import { trafficPreviewUrl } from './traffic-preview'
import {
  page,
  header,
  titleBlock,
  title,
  subtitle,
  table,
  tableCard,
  tableViewport,
  th,
  td,
} from './admin.css'
import * as styles from './checkout-traffic.css'

type Selection = { id: string; name: string }
type MainView = 'players' | 'pages'
type PlayerView = 'pages' | 'products' | 'offers'
type Sort = 'traffic' | 'name' | 'recent'
type PageSize = 20 | 50 | 100

const count = (value?: string) => BigInt(value ?? '0').toLocaleString('pt-BR')
const formatDateTime = (value?: string) =>
  value
    ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(
        new Date(value),
      )
    : 'Sem registro'
const sourceParts = (value: string | null) => {
  if (!value) return { title: 'Origem indisponível', detail: 'O navegador não informou a origem' }
  const url = new URL(value)
  return { title: url.host, detail: `${url.pathname}${url.search}` || '/' }
}

export function CheckoutTrafficScreen() {
  const [mainView, setMainView] = useState<MainView>('players')
  const [playerView, setPlayerView] = useState<PlayerView>('pages')
  const [player, setPlayer] = useState<Selection | null>(null)
  const [product, setProduct] = useState<Selection | null>(null)
  const [offer, setOffer] = useState<Selection | null>(null)
  const [queryDraft, setQueryDraft] = useState('')
  const [query, setQuery] = useState('')
  const [searchField, setSearchField] = useState<'all' | 'id' | 'name' | 'url'>('all')
  const [sourceKind, setSourceKind] = useState<'all' | TrafficItem['sourceKind']>('all')
  const [sort, setSort] = useState<Sort>('traffic')
  const [pageSize, setPageSize] = useState<PageSize>(20)
  const [offset, setOffset] = useState(0)
  const [preview, setPreview] = useState<TrafficItem | null>(null)
  const [previewVersion, setPreviewVersion] = useState(0)
  const [selectedSource, setSelectedSource] = useState<TrafficItem | null>(null)
  const [accessOffset, setAccessOffset] = useState(0)

  const view: TrafficView = player ? playerView : mainView
  const scope: TrafficFilters = {
    producerId: player?.id ?? '',
    productId: product?.id ?? '',
    offerId: offer?.id ?? '',
  }
  const listing = useCheckoutTraffic(scope, view, offset, {
    search: query || undefined,
    searchBy: searchField,
    sourceKind: view === 'pages' && sourceKind !== 'all' ? sourceKind : undefined,
    sort,
    limit: pageSize,
  })
  const accesses = useCheckoutTraffic(scope, 'accesses', accessOffset, {
    sourcePage: selectedSource?.sourcePage ?? undefined,
    sourceKind: selectedSource?.sourceKind,
    limit: 25,
  })
  const total =
    listing.data?.total ??
    offset + (listing.data?.items.length ?? 0) + (listing.data?.hasMore ? 1 : 0)
  const scopeName = offer?.name ?? product?.name ?? player?.name ?? 'Todos os players'
  const previewUrl = trafficPreviewUrl(preview?.sourcePage ?? null, window.location.origin)
  const isPages = view === 'pages'

  const resetFilters = () => {
    setQueryDraft('')
    setQuery('')
    setSearchField('all')
    setSourceKind('all')
    setSort('traffic')
    setOffset(0)
  }
  const closeOverlays = () => {
    setPreview(null)
    setSelectedSource(null)
    setAccessOffset(0)
  }
  const changeMainView = (next: MainView) => {
    setMainView(next)
    setPlayer(null)
    setProduct(null)
    setOffer(null)
    resetFilters()
    closeOverlays()
  }
  const changePlayerView = (next: PlayerView) => {
    setPlayerView(next)
    if (next !== 'offers') setProduct(null)
    setOffer(null)
    resetFilters()
    closeOverlays()
  }
  const applySearch = (event: FormEvent) => {
    event.preventDefault()
    setQuery(queryDraft.trim())
    setOffset(0)
  }
  const selectPlayer = (item: TrafficItem) => {
    if (!item.id) return
    setPlayer({ id: item.id, name: item.name || 'Player sem nome cadastrado' })
    setPlayerView('pages')
    setProduct(null)
    setOffer(null)
    resetFilters()
  }
  const selectProduct = (item: TrafficItem) => {
    if (!item.id) return
    setProduct({ id: item.id, name: item.name || 'Produto sem nome cadastrado' })
    setPlayerView('offers')
    setOffer(null)
    resetFilters()
  }
  const showOfferPages = (item: TrafficItem) => {
    if (!item.id) return
    setOffer({ id: item.id, name: item.name || 'Oferta sem nome cadastrado' })
    setPlayerView('pages')
    resetFilters()
  }
  const clearNavigationScope = () => {
    setProduct(null)
    setOffer(null)
    setOffset(0)
    closeOverlays()
  }

  return (
    <section className={page} data-testid="checkout-traffic-screen">
      <div className={header}>
        <div className={titleBlock}>
          <h1 className={title}>Origens do checkout</h1>
          <p className={subtitle}>
            Acompanhe as chegadas ao checkout por player, produto, oferta e página observada.
          </p>
        </div>
      </div>
      <Banner tone="info" title="Análise interna do suporte">
        Tráfego representa chegadas ao checkout, não vendas. A origem observada é um indício
        operacional e não confirma fraude.
      </Banner>

      {!player ? (
        <SegmentedControl
          value={mainView}
          options={[
            { value: 'players', label: 'Players' },
            { value: 'pages', label: 'Páginas' },
          ]}
          onChange={changeMainView}
          ariaLabel="Área de análise"
        />
      ) : (
        <Card padding="md" className={styles.identityCard}>
          <Button
            variant="secondary"
            className={styles.backButton}
            onClick={() => changeMainView('players')}
          >
            <ArrowLeft size={16} aria-hidden /> Voltar para todos os players
          </Button>
          <div className={styles.identity}>
            <span className={styles.avatar} aria-hidden>
              {player.name.slice(0, 2).toUpperCase()}
            </span>
            <div>
              <span className={styles.contextLabel}>Analisando player</span>
              <h2 className={styles.identityTitle}>{player.name}</h2>
              <p className={styles.identityMeta}>{player.id}</p>
            </div>
          </div>
          <SegmentedControl
            value={playerView}
            options={[
              { value: 'pages', label: 'Páginas' },
              { value: 'products', label: 'Produtos' },
              { value: 'offers', label: 'Ofertas' },
            ]}
            onChange={changePlayerView}
            ariaLabel="Detalhes do player"
          />
        </Card>
      )}

      <Card padding="none" className={`${tableCard} ${styles.panel}`}>
        {player && (product || offer) && (
          <div className={styles.navigationScope} role="status">
            <div>
              <strong className={styles.navigationScopeTitle}>Recorte atual</strong>
              <span className={styles.navigationScopeText}>
                {offer
                  ? `Páginas da oferta “${offer.name}”${product ? `, do produto “${product.name}”` : ''}.`
                  : `Ofertas do produto “${product?.name}”.`}
              </span>
            </div>
            <Button variant="secondary" onClick={clearNavigationScope}>
              <X size={16} aria-hidden />
              {view === 'pages' ? 'Ver todas as páginas do player' : 'Ver todas as ofertas'}
            </Button>
          </div>
        )}
        <form className={styles.toolbar} onSubmit={applySearch}>
          {!isPages && (
            <SelectField
              label="Buscar por"
              value={searchField}
              onChange={(event) => setSearchField(event.target.value as typeof searchField)}
              options={
                view === 'players'
                  ? [
                      { value: 'all', label: 'Nome, e-mail ou ID' },
                      { value: 'id', label: 'Identificador' },
                    ]
                  : [
                      { value: 'all', label: 'Nome ou ID' },
                      { value: 'name', label: 'Nome' },
                      { value: 'id', label: 'Identificador' },
                    ]
              }
            />
          )}
          <div className={styles.searchControl}>
            <SearchField
              value={queryDraft}
              onChange={setQueryDraft}
              ariaLabel={
                isPages
                  ? 'Encontrar página'
                  : `Encontrar ${view === 'players' ? 'player' : view === 'products' ? 'produto' : 'oferta'}`
              }
              placeholder={
                isPages
                  ? 'Ex.: academia.example ou /inscricao'
                  : view === 'players'
                    ? 'Ex.: Horizonte, contato@empresa.com ou ID'
                    : 'Digite o nome ou ID'
              }
            />
          </div>
          {isPages && (
            <SelectField
              label="Identificação"
              value={sourceKind}
              onChange={(event) => {
                setSourceKind(event.target.value as typeof sourceKind)
                setOffset(0)
              }}
              options={[
                { value: 'all', label: 'Todas' },
                { value: 'PAGE', label: 'Página informada' },
                { value: 'ORIGIN_ONLY', label: 'Somente domínio' },
                { value: 'UNAVAILABLE', label: 'Origem indisponível' },
              ]}
            />
          )}
          <Button type="submit">
            <Search size={16} aria-hidden /> Buscar
          </Button>
          {(query || sourceKind !== 'all') && (
            <Button type="button" variant="text" onClick={resetFilters}>
              Limpar filtros
            </Button>
          )}
        </form>

        <div className={styles.meta}>
          <span role="status">
            {total.toLocaleString('pt-BR')}{' '}
            {view === 'players'
              ? 'players'
              : view === 'pages'
                ? 'páginas'
                : view === 'products'
                  ? 'produtos'
                  : 'ofertas'}
            {query ? ` · busca: ${query}` : ''}
          </span>
          <SelectField
            label="Ordenar"
            value={sort}
            onChange={(event) => {
              setSort(event.target.value as Sort)
              setOffset(0)
            }}
            options={[
              { value: 'traffic', label: 'Mais tráfego' },
              { value: 'name', label: isPages ? 'URL A–Z' : 'Nome A–Z' },
              { value: 'recent', label: 'Chegada mais recente' },
            ]}
          />
        </div>

        {listing.isPending ? (
          <div className={styles.state}>
            <Skeleton height={260} />
          </div>
        ) : listing.isError ? (
          <div className={styles.state}>
            <ErrorState
              title="Não foi possível carregar a análise"
              description="Tente novamente para consultar o tráfego do checkout."
              onRetry={() => void listing.refetch()}
            />
          </div>
        ) : !listing.data.items.length ? (
          <div className={styles.state}>
            <EmptyState
              icon={<Route size={24} />}
              title="Nenhum resultado"
              description="Revise a busca ou limpe os filtros para ver a lista novamente."
            />
          </div>
        ) : (
          <TrafficTable
            view={view}
            items={listing.data.items}
            onPlayer={selectPlayer}
            onProduct={selectProduct}
            onOffer={showOfferPages}
            onAccesses={(item) => {
              setSelectedSource(item)
              setAccessOffset(0)
            }}
            onPreview={(item) => {
              setPreview(item)
              setPreviewVersion(0)
            }}
          />
        )}

        <div className={styles.footer}>
          <span>
            {total ? offset + 1 : 0}–{Math.min(offset + pageSize, total)} de{' '}
            {total.toLocaleString('pt-BR')}
          </span>
          <div className={styles.pagination}>
            <SelectField
              className={styles.pageSize}
              label="Por página"
              value={String(pageSize)}
              onChange={(event) => {
                setPageSize(Number(event.target.value) as PageSize)
                setOffset(0)
              }}
              options={[20, 50, 100].map((value) => ({
                value: String(value),
                label: String(value),
              }))}
            />
            <Button
              variant="secondary"
              disabled={!offset || listing.isFetching}
              onClick={() => setOffset(Math.max(0, offset - pageSize))}
            >
              Anterior
            </Button>
            <span>Página {Math.floor(offset / pageSize) + 1}</span>
            <Button
              variant="secondary"
              disabled={!listing.data?.hasMore || listing.isFetching}
              onClick={() => setOffset(offset + pageSize)}
            >
              Próxima
            </Button>
          </div>
        </div>
      </Card>

      <p className={styles.help}>
        Uma linha por cadastro ou origem. Acessos individuais ficam no detalhe de cada página.
      </p>

      <AccessDrawer
        item={selectedSource}
        accesses={accesses}
        offset={accessOffset}
        onOffset={setAccessOffset}
        onClose={() => setSelectedSource(null)}
      />
      <Drawer
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        title="Prévia da origem"
        description={scopeName}
        size="audit"
      >
        {preview && (
          <div className={styles.detail}>
            <p className={styles.url}>{preview.sourcePage}</p>
            <p>
              <strong>{count(preview.redirects)}</strong> {redirectLabel(preview.redirects)} neste
              recorte
            </p>
            <Banner tone="info" title="Conteúdo atual do site externo">
              Esta prévia não é uma captura do momento do acesso. Sites podem bloquear a exibição ou
              aparecer incompletos.
            </Banner>
            <Button variant="secondary" onClick={() => setPreviewVersion((value) => value + 1)}>
              Recarregar prévia
            </Button>
            {previewUrl ? (
              <iframe
                key={previewVersion}
                className={styles.preview}
                title="Página de origem em prévia isolada"
                src={previewUrl}
                sandbox="allow-scripts"
                referrerPolicy="no-referrer"
                allow="camera 'none'; microphone 'none'; geolocation 'none'; payment 'none'; clipboard-read 'none'; clipboard-write 'none'"
              />
            ) : (
              <EmptyState
                icon={<Eye size={24} />}
                title="Prévia indisponível"
                description="A prévia aceita apenas páginas HTTPS externas e seguras."
              />
            )}
          </div>
        )}
      </Drawer>
    </section>
  )
}

function TrafficTable({
  view,
  items,
  onPlayer,
  onProduct,
  onOffer,
  onAccesses,
  onPreview,
}: {
  view: TrafficView
  items: TrafficItem[]
  onPlayer: (item: TrafficItem) => void
  onProduct: (item: TrafficItem) => void
  onOffer: (item: TrafficItem) => void
  onAccesses: (item: TrafficItem) => void
  onPreview: (item: TrafficItem) => void
}) {
  return (
    <div className={tableViewport}>
      <table className={`${table} ${styles.dataTable}`}>
        <thead>
          <tr>
            <th className={th}>
              {view === 'pages'
                ? 'Página de origem'
                : view === 'players'
                  ? 'Player'
                  : view === 'products'
                    ? 'Produto'
                    : 'Oferta'}
            </th>
            <th className={th}>
              {view === 'players' ? 'Produtos' : view === 'pages' ? 'Escopo' : 'Origens'}
            </th>
            <th className={th}>Tráfego</th>
            <th className={th}>Última chegada</th>
            <th className={th}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const source = sourceParts(item.sourcePage)
            const label =
              item.name || (view === 'offers' ? 'Oferta não identificada' : 'Nome indisponível')
            return (
              <tr key={item.id ?? item.sourcePage ?? `unknown-${index}`}>
                <td className={`${td} ${styles.primaryCell}`}>
                  <strong>{view === 'pages' ? source.title : label}</strong>
                  <span className={styles.cellHint}>
                    {view === 'pages'
                      ? `${source.detail} · ${item.sourceKind === 'PAGE' ? 'Página informada' : item.sourceKind === 'ORIGIN_ONLY' ? 'Somente domínio' : 'Origem indisponível'}`
                      : item.id}
                  </span>
                </td>
                <td className={td}>
                  {view === 'players'
                    ? count(item.productCount)
                    : view === 'pages'
                      ? `${count(item.playerCount)} players · ${count(item.productCount)} produtos`
                      : `${count(item.pageCount)} origens`}
                </td>
                <td className={td}>
                  <strong>{count(item.redirects)}</strong>
                  <span className={styles.cellHint}>{redirectLabel(item.redirects)}</span>
                </td>
                <td className={td}>{formatDateTime(item.lastArrivedAt)}</td>
                <td className={td}>
                  <div className={styles.rowActions}>
                    {view === 'players' && (
                      <Button variant="text" onClick={() => onPlayer(item)}>
                        Analisar <ArrowRight size={16} aria-hidden />
                      </Button>
                    )}
                    {view === 'products' && (
                      <Button variant="text" onClick={() => onProduct(item)}>
                        Ver ofertas <ArrowRight size={16} aria-hidden />
                      </Button>
                    )}
                    {view === 'offers' && (
                      <Button variant="text" onClick={() => onOffer(item)}>
                        Ver origens <ArrowRight size={16} aria-hidden />
                      </Button>
                    )}
                    {view === 'pages' && (
                      <>
                        <Button variant="secondary" onClick={() => onAccesses(item)}>
                          Ver acessos
                        </Button>
                        {item.sourcePage && (
                          <Button variant="secondary" onClick={() => onPreview(item)}>
                            <Eye size={16} aria-hidden /> Prévia
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function AccessDrawer({
  item,
  accesses,
  offset,
  onOffset,
  onClose,
}: {
  item: TrafficItem | null
  accesses: ReturnType<typeof useCheckoutTraffic>
  offset: number
  onOffset: (value: number) => void
  onClose: () => void
}) {
  return (
    <Drawer
      open={Boolean(item)}
      onClose={onClose}
      title="Acessos individuais"
      description={item?.sourcePage ?? 'Origem não informada pelo navegador'}
      size="audit"
    >
      {item && (
        <div className={styles.detail}>
          <Banner tone="info" title="Evidência disponível">
            Cada linha representa uma chegada registrada no checkout. A comparação de domínios é um
            indício operacional e não confirma fraude.
          </Banner>
          {accesses.isPending ? (
            <Skeleton height={220} />
          ) : accesses.isError ? (
            <ErrorState
              title="Não foi possível carregar os acessos"
              description="Tente novamente para consultar os registros desta origem."
              onRetry={() => void accesses.refetch()}
            />
          ) : !accesses.data?.items.length ? (
            <EmptyState
              icon={<Route size={24} />}
              title="Nenhum acesso individual encontrado"
              description="Atualize a consulta ou escolha outra origem."
            />
          ) : (
            <div className={styles.accessList}>
              {accesses.data.items.map((access) => (
                <Card key={access.arrivalId} padding="md" className={styles.accessCard}>
                  <div>
                    <strong>{formatDateTime(access.arrivedAt)}</strong>
                    <span className={styles.cellHint}>Acesso {access.arrivalId}</span>
                  </div>
                  <dl className={styles.accessDetails}>
                    <div>
                      <dt className={styles.accessTerm}>Produto</dt>
                      <dd className={styles.accessValue}>
                        {access.productName || 'Produto não identificado'}
                      </dd>
                    </div>
                    <div>
                      <dt className={styles.accessTerm}>Oferta</dt>
                      <dd className={styles.accessValue}>
                        {access.offerName || 'Oferta não identificada'}
                      </dd>
                    </div>
                    <div>
                      <dt className={styles.accessTerm}>Landing page declarada</dt>
                      <dd className={styles.accessValue}>
                        {access.declaredPage || 'Não cadastrada'}
                      </dd>
                    </div>
                    <div>
                      <dt className={styles.accessTerm}>Comparação</dt>
                      <dd className={styles.accessValue}>
                        {declaredPageComparison(access.declaredPage, access.sourcePage)}
                      </dd>
                    </div>
                  </dl>
                </Card>
              ))}
            </div>
          )}
          <div className={styles.pagination}>
            <Button
              variant="secondary"
              disabled={!offset || accesses.isFetching}
              onClick={() => onOffset(Math.max(0, offset - 25))}
            >
              Acessos anteriores
            </Button>
            <span>Página {offset / 25 + 1}</span>
            <Button
              variant="secondary"
              disabled={!accesses.data?.hasMore || accesses.isFetching}
              onClick={() => onOffset(offset + 25)}
            >
              Mais acessos
            </Button>
          </div>
        </div>
      )}
    </Drawer>
  )
}
